import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { addTaskComment, listTaskComments } from '@/lib/api/generated/api';
import { expectStatus } from '@/lib/api/client';
import type {
  TaskCommentList,
  TaskCommentView,
} from '@/lib/api/generated/model';
import { taskKeys } from '../queryKeys';

/**
 * One task's timeline, fetched when the task is open and not before.
 *
 * Not part of the board's aggregate read on purpose: that one re-fetches every
 * two seconds to keep card status fresh and carries every task in the
 * workspace. Comment history has no upper bound, so putting it there would grow
 * a hot polling payload for data almost no reader wants. This has no
 * `refetchInterval` for the same reason — a conversation does not need polling,
 * and writing a comment updates the cache directly.
 */
export function useTaskComments(
  workspaceId: string,
  taskId: string | undefined,
) {
  return useQuery({
    queryKey: taskKeys.comments(workspaceId, taskId ?? ''),
    queryFn: async (): Promise<TaskCommentList> =>
      expectStatus(
        await listTaskComments(workspaceId, taskId ?? ''),
        200,
        'The comments could not be loaded.',
      ),
    enabled: taskId !== undefined,
  });
}

export interface AddTaskCommentInput {
  workspaceId: string;
  taskId: string;
  body: string;
}

/**
 * Write a comment onto a task's timeline.
 *
 * The response carries the comment exactly as stored — its identifier and the
 * time the database actually assigned — so it is appended to the cache directly
 * rather than triggering a refetch that could only return the same thing.
 */
export function useAddTaskComment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      workspaceId,
      taskId,
      body,
    }: AddTaskCommentInput): Promise<TaskCommentView> =>
      expectStatus(
        await addTaskComment(workspaceId, taskId, { body }),
        201,
        'The comment could not be added.',
      ),
    onSuccess: async (comment, { workspaceId, taskId }) => {
      const queryKey = taskKeys.comments(workspaceId, taskId);
      // A refetch already in flight took its snapshot before this write
      // landed. Left alone, it settles after the append below and replaces
      // the list with one that predates the comment — the row vanishes with
      // nothing to bring it back, since this query does not poll. Cancel it
      // so the append is the last thing the cache hears.
      await queryClient.cancelQueries({ queryKey });
      const prev = queryClient.getQueryData<TaskCommentList>(queryKey);
      if (prev) {
        queryClient.setQueryData<TaskCommentList>(queryKey, {
          ...prev,
          items: [...prev.items, comment],
        });
        return;
      }
      // A cache with nothing in it yet has nothing to append to — fetch it
      // rather than seeding a list this mutation cannot know is complete.
      // Only then: a refetch after a successful append is the same race
      // reopened, and can only return what the append already put there.
      await queryClient.invalidateQueries({ queryKey });
    },
  });
}
